import axios from 'axios';
import { memo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useIban } from '@/hooks/api/use-iban';

import SmallSpinner from '@/components/shared/spinners/small-spinner';
import Text from '@/components/forms/text';

interface IbanFieldProps {
    /** Nom du champ IBAN dans le formulaire. */
    nameIban?: string;
}

/**
 * IBAN, contrôlé à la saisie auprès du web service « services », qui renseigne la
 * domiciliation et le BIC du formulaire parent.
 */
const IbanField = memo(
    ({ nameIban = 'iban' }: IbanFieldProps) => {
        const {
            watch,
            setValue,
            formState: { errors, touchedFields },
        } = useFormContext();
        const ibanValue = watch(nameIban) as string | undefined;
        const domiciliationValue = watch('domiciliation') as string | undefined;
        const bicValue = watch('bic') as string | undefined;
        const queryIBAN = useIban(ibanValue);

        const errorMessage = axios.isAxiosError(queryIBAN.error)
            ? (queryIBAN.error.response?.data as { message?: string } | undefined)?.message
            : undefined;

        useEffect(() => {
            if (queryIBAN.isSuccess && queryIBAN.data?.['bic']) {
                setValue('bic', queryIBAN.data['bic']);
                setValue('domiciliation', queryIBAN.data['bank']);
            } else if (queryIBAN.isError && !queryIBAN.isFetching) {
                setValue('bic', '');
                setValue('domiciliation', '');
            }
        }, [queryIBAN.isSuccess, queryIBAN.isError]);

        const touched = (touchedFields as Record<string, boolean | undefined>)[nameIban];
        const fieldError = errors[nameIban]?.message as string | undefined;

        return (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <Text
                        label={'IBAN'}
                        name={nameIban}
                        value={ibanValue || ''}
                        onChange={(e) => {
                            setValue(nameIban, e.target.value, { shouldValidate: true });
                            setValue('bic', '');
                            setValue('domiciliation', '');
                        }}
                        error={touched && (fieldError || (queryIBAN.isError && !queryIBAN.isFetching && errorMessage))}
                    />
                    {queryIBAN.isLoading && <SmallSpinner />}
                </div>
                <div>
                    <Text
                        label={'Domiciliation de la banque'}
                        name="domiciliation"
                        value={domiciliationValue || ''}
                        disabled
                        normalize={(v) => v.toUpperCase()}
                    />
                </div>
                <div>
                    <Text label={'Code BIC'} name="bic" value={bicValue || ''} disabled normalize={(v) => v.toUpperCase()} />
                </div>
            </div>
        );
    },
    // `values` n'est jamais passé : la comparaison vaut toujours vrai et le champ ne se re-rend
    // que sur ses propres abonnements au formulaire (`watch`). Conservé tel quel.
    (prevProps, nextProps) => (prevProps as { values?: unknown }).values === (nextProps as { values?: unknown }).values,
);

export default IbanField;
